/**
 * CombatModule - Root orchestrator for DAoC Combat Engine
 */

import { EntityState, Direction8 } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';
import { ThreatTable } from './ThreatTable';
import { Skills } from './SkillEngine';
import { CrowdControl } from './CCStateMachine';
import { FormationAI } from './PartyFormationAI';
import { ResourceRegen } from './ResourceRegen';
import { Concentration } from './ConcentrationPool';
import { getDerivedCombatMetrics, getEffectiveMoveSpeed, practiceSelfSkill, adjustEntityFame, addItemToInventory, distributeLootToParty } from '../character/CharacterSystem';
import { RARITY_TIERS, InventoryItem } from '../../core/Types';
import { ZoneData, getZoneData } from '../world/ZoneMapData';
import { applyTileEnvironmentEffects } from '../world/TilePropertySystem';
import { Currency } from '../../core/Currency';

export class CombatModule {
  private registry: EntityRegistry;
  private isStickActive = false;
  private wasInCombat = false;

  constructor(registry: EntityRegistry) {
    this.registry = registry;
    this.initEventBindings();
  }

  private initEventBindings(): void {
    // Combat hit logging
    EventBus.on('COMBAT_HIT', (e) => {
      let icon = '💥';
      if (e.damageType === 'slash') icon = '🗡️';
      else if (e.damageType === 'crush') icon = '🔨';
      else if (e.damageType === 'thrust') icon = '🏹';
      else if (e.damageType === 'holy') icon = '✨';

      const ffPrefix = e.isFriendlyFire ? '⚠️ [아군 오인 사격!]' : '';
      const text = `${icon} ${ffPrefix} [${e.attackerName}] -> [${e.targetName}] ${e.damage} 피해 (${e.damageType.toUpperCase()})`;

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text,
        category: 'combat',
      });
    });

    // Cast interrupted logging
    EventBus.on('CAST_INTERRUPTED', (e) => {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💥 [${e.entityName}] 의 [${e.spellName}] 시전이 피격/이동으로 즉시 취소되었습니다! (INTERRUPTED!)`,
        category: 'interrupted',
      });
    });

    // Heal logging
    EventBus.on('HEAL_APPLIED', (e) => {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💚 [${e.sourceName}] -> [${e.targetName}] +${e.amount} HP 회복!`,
        category: 'heal',
      });
    });

    // Mez broken logging
    EventBus.on('MEZ_BROKEN', (e) => {
      const target = this.registry.get(e.targetId);
      const name = target ? target.name : '대상';
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💤💥 [${name}] 의 수면(Mez)이 ${e.breakerName}의 공격 피격으로 즉시 해제되었습니다!`,
        category: 'combat',
      });
    });

    // CC Applied logging
    EventBus.on('CC_APPLIED', (e) => {
      const target = this.registry.get(e.targetId);
      const name = target ? target.name : '대상';
      let ccName = '군중제어';
      if (e.ccType === 'stun') ccName = '기절(Stun)';
      else if (e.ccType === 'mez') ccName = '수면(Mez)';
      else if (e.ccType === 'root') ccName = '이동불가(Root)';
      else if (e.ccType === 'snare') ccName = '이속감소(Snare)';

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚡ [${name}] 에게 ${ccName} 적용 (${e.duration.toFixed(1)}초)`,
        category: 'system',
      });
    });

    // Buff applied / removed logging
    EventBus.on('BUFF_APPLIED', (e) => {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `✨ [${e.buff.name}] 버프 활성화 (집중력 ${e.buff.concCost} 보존)`,
        category: 'system',
      });
    });

    EventBus.on('BUFF_REMOVED', (e) => {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💨 [${e.buffId}] 버프 해제 (집중력 ${e.concReturned} 반환)`,
        category: 'system',
      });
    });

    // Unit died logging, Boss Fame Reward & target clear
    EventBus.on('UNIT_DIED', (e) => {
      const victim = this.registry.get(e.victimId);
      const name = victim ? victim.name : '대상';
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💀 [${name}] 처치되었습니다!`,
        category: 'system',
      });

      // Award Fame, Currency (자동 균등분배) & Drops (파티 리더 인벤토리 직행) when hunting Monsters!
      if (victim && victim.category === 'monster') {
        const isBoss = victim.id.includes('boss') || victim.resources.maxHp >= 1000;
        const fameGain = isBoss ? 250 : 80;
        const totalCopperGain = isBoss ? Currency.toCopper({ gold: 10 }) : Currency.toCopper({ silver: 50 });
        const party = this.registry.getParty();
        const partySize = Math.max(1, party.length);
        const splitCopper = Math.max(1, Math.floor(totalCopperGain / partySize));
        const leader = this.registry.getPlayer();

        // 1. 파티 사냥 시 돈은 자동 균등 분배 (1/N)
        for (const member of party) {
          member.fame = (member.fame ?? 120) + fameGain;
          member.gold = (member.gold ?? Currency.toCopper({ gold: 100 })) + splitCopper;
        }

        if (leader) {
          adjustEntityFame(
            leader,
            0,
            `${isBoss ? '보스몹' : '엘리트몹'} [${name}] 토벌! (개인당 명성 +${fameGain}, 균등 분배 +${Currency.format(splitCopper)})`
          );

          // 2. 전리품 아이템은 모두 파티 리더의 100칸 인벤토리로 자동 지급
          const drops = isBoss
            ? [
                {
                  id: `loot_drop_${Date.now()}_1`,
                  name: '고대 드래곤 비늘',
                  icon: '🐉',
                  category: 'material' as const,
                  rarity: 'legendary' as const,
                  quantity: 1,
                  maxStack: 20,
                  weightKg: 0.5,
                  valueGold: Currency.toCopper({ gold: 10 }),
                  description: '고대 보스 몬스터로부터 획득한 귀중한 전리품 재료.',
                },
                {
                  id: `loot_drop_${Date.now()}_2`,
                  name: '특급 생명력 물약',
                  icon: '🧪',
                  category: 'consumable' as const,
                  rarity: 'fine' as const,
                  quantity: 3,
                  maxStack: 99,
                  weightKg: 0.1,
                  valueGold: Currency.toCopper({ gold: 100 }),
                  description: '즉시 150 HP를 회복합니다. (개당 100골드)',
                },
              ]
            : [
                {
                  id: `loot_drop_${Date.now()}_3`,
                  name: '몬스터 전리품 가죽',
                  icon: '🥩',
                  category: 'material' as const,
                  rarity: 'common' as const,
                  quantity: 2,
                  maxStack: 50,
                  weightKg: 0.2,
                  valueGold: Currency.toCopper({ silver: 10 }),
                  description: '야생 몬스터 사냥 후 수습한 무두질용 원단.',
                },
              ];

          // 2. 전리품 아이템 분배 로직 (파티 리더 인벤토리 등급 필터 및 미만 등급 자동 분배)
          const filterRarity = leader.lootFilterRarity || 'common';
          const minTier = RARITY_TIERS[filterRarity] || 1;

          // Collect current combat threat map across all party members
          const threatMap: Record<string, number> = {};
          if (victim.threatTable) {
            for (const mId in victim.threatTable) {
              threatMap[mId] = victim.threatTable[mId];
            }
          }
          // Also calculate general threat from engaged monsters
          const allMonsters = this.registry.getMonsters();
          for (const member of party) {
            threatMap[member.id] = (threatMap[member.id] || 0) + ThreatTable.getTotalThreat(member.id, allMonsters);
          }

          for (const dropItem of drops) {
            const itemTier = RARITY_TIERS[dropItem.rarity] || 1;

            if (itemTier >= minTier) {
              // 설정된 등급 이상: 파티 리더의 인벤토리로 우선 수납
              const addedToLeader = addItemToInventory(leader, dropItem as InventoryItem);
              if (addedToLeader) {
                EventBus.emit('LOG_EMITTED', {
                  id: Math.random().toString(),
                  timestamp: performance.now(),
                  text: `👑📦 [리더 인벤 수납] 파티 리더 [${leader.name}]의 인벤토리에 설정 등급 이상 아이템 [${dropItem.icon} ${dropItem.name} ×${dropItem.quantity}]이(가) 수납되었습니다!`,
                  category: 'system',
                });
              } else {
                // 리더 인벤이 가득 찬 경우 파티원에게 자동 분배
                const distResult = distributeLootToParty(party, dropItem as InventoryItem, threatMap);
                if (distResult) {
                  EventBus.emit('LOG_EMITTED', {
                    id: Math.random().toString(),
                    timestamp: performance.now(),
                    text: `⚠️ [리더 가방 만석 자동분배] [${dropItem.icon} ${dropItem.name} ×${dropItem.quantity}] ➔ [${distResult.target.name}] 수납 완료! ${distResult.reason}`,
                    category: 'system',
                  });
                }
              }
            } else {
              // 설정된 등급 미만: 파티원 자동분배 규칙 가동 (1순위: 직업 적합, 2순위: 어그로 순위)
              const distResult = distributeLootToParty(party, dropItem as InventoryItem, threatMap);
              if (distResult) {
                EventBus.emit('LOG_EMITTED', {
                  id: Math.random().toString(),
                  timestamp: performance.now(),
                  text: `🎲👥 [등급 미만 자동분배] [${dropItem.icon} ${dropItem.name} ×${dropItem.quantity}] ➔ [${distResult.target.name}] (${distResult.target.job || distResult.target.role}) 지급 완료! ${distResult.reason}`,
                  category: 'system',
                });
              } else {
                // 모든 파티원 인벤토리가 가득 찬 경우 최종적으로 리더에게 시도
                addItemToInventory(leader, dropItem as InventoryItem);
              }
            }
          }
        }
      }

      // Clear player target and cancel stick if victim was current target
      const player = this.registry.getPlayer();
      if (player && player.targetId === e.victimId) {
        this.cancelStick(true, 'target_dead_or_lost');
      }
    });
  }

  public update(dt: number, activeZone?: ZoneData): void {
    const player = this.registry.getPlayer();
    if (!player) return;
    const zone = activeZone || getZoneData(1);

    // Check if Bard Hymn of Vigor is currently active in the party
    const party = this.registry.getParty();
    const hasBardVigor = party.some((p) =>
      p.buffs.some((b) => b.id === 'hymn_of_vigor')
    );

    // 1. Update all entities: cooldowns, CC, casts, regen, attack animations, and Tile Hazards/DOTs!
    const allEntities = this.registry.getAll();
    for (const e of allEntities) {
      if (e.isDead) continue;
      if (e.attackAnimTimer && e.attackAnimTimer > 0) {
        e.attackAnimTimer -= dt;
      }
      Skills.update(e, dt, this.registry);
      ResourceRegen.update(e, dt, hasBardVigor);

      // Apply Tile Property Hazards (Lava direct burn + Burn DOT, Swamp Poison DOT, Altar/Crystal Regen, Snow/Sand SP drain)
      applyTileEnvironmentEffects(e, zone, dt, (victim, dmg, label, tileType) => {
        if (victim.id === player.id) {
          EventBus.emit('COMBAT_HIT', {
            attackerId: `tile_${tileType}`,
            attackerName: label,
            targetId: victim.id,
            targetName: victim.name,
            damage: dmg,
            damageType: tileType === 'lava' ? 'holy' : 'crush',
            isCrit: false,
            isFriendlyFire: false,
          });
        }
      });
    }

    // 2. Update monster AI (Threat table targeting and attacks)
    this.updateMonsters(dt, player);

    // 3. Update companion AI (Formation follow out-of-combat / DAoC roles in-combat)
    FormationAI.updateCompanions(dt, player, this.registry);

    // 4. Update Party & Player Automatic Weapon Attacks
    this.updatePartyAutoAttacks(dt, player);

    // 5. Update despawns
    this.registry.updateDespawns(performance.now());

    // 6. Check party combat state transition (Auto Zoom Trigger to 150%)
    const activeCombatTarget = FormationAI.getPartyCombatTarget(player, this.registry.getMonsters());
    if (!activeCombatTarget && player.inCombat) {
      const hasAggro = this.registry.getMonsters().some((m) => !m.isDead && (m.threatTable?.[player.id] || 0) > 0);
      if (!hasAggro) {
        player.inCombat = false;
      }
    }

    const currentInCombat = this.isPartyInCombat();
    if (currentInCombat !== this.wasInCombat) {
      this.wasInCombat = currentInCombat;
      EventBus.emit('COMBAT_STATE_CHANGED', { inCombat: currentInCombat });
      if (currentInCombat) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: '⚔️ [전투 돌입] 전투가 시작되어 화면을 150%로 자동 줌인합니다!',
          category: 'combat',
        });
      } else {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: '🕊️ [전투 종료] 전투가 종료되어 기본 시점으로 줌아웃합니다.',
          category: 'system',
        });
      }
    }
  }

  public isPartyInCombat(): boolean {
    const player = this.registry.getPlayer();
    const party = this.registry.getParty();
    if (party.some((p) => p.inCombat)) return true;

    const monsters = this.registry.getMonsters();
    for (const m of monsters) {
      if (m.isDead || !m.threatTable) continue;
      for (const p of party) {
        if ((m.threatTable[p.id] || 0) > 0) return true;
      }
    }

    if (player && player.targetId) {
      const t = this.registry.get(player.targetId);
      if (t && t.category === 'monster' && !t.isDead && (player.inCombat || t.inCombat)) {
        return true;
      }
    }

    return false;
  }

  private updateMonsters(dt: number, player: EntityState): void {
    const monsters = this.registry.getMonsters();

    for (const monster of monsters) {
      if (monster.isDead) continue;
      if (!CrowdControl.canAct(monster)) continue;

      // Find primary threat target
      let primaryTarget = ThreatTable.getHighestThreatTarget(monster, this.registry);

      // If no aggro yet, monster idles or attacks player if within 2.5 tiles
      if (!primaryTarget) {
        const distToPlayer = this.registry.getDistance(monster, player);
        if (distToPlayer <= 2.8 && !player.isDead) {
          ThreatTable.addThreat(monster, player.id, 50);
          primaryTarget = player;
          monster.inCombat = true;
        } else {
          monster.inCombat = false;
          continue;
        }
      }

      monster.inCombat = true;
      monster.targetId = primaryTarget.id;

      // Move toward primary target
      const dist = this.registry.getDistance(monster, primaryTarget);
      const meleeRange = 1.35;

      if (dist > meleeRange && CrowdControl.canMove(monster)) {
        const dx = primaryTarget.x - monster.x;
        const dy = primaryTarget.y - monster.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const effSpeed = getEffectiveMoveSpeed(monster);
        monster.x += (dx / len) * effSpeed * dt;
        monster.y += (dy / len) * effSpeed * dt;
      }

      // Attack primary target if in range
      if (dist <= meleeRange + 0.3) {
        if (!monster.cooldowns[0] || monster.cooldowns[0] <= 0) {
          const dmg = 22;
          monster.attackAnimTimer = 0.25;
          monster.dir = this.calcDirection(primaryTarget.x - monster.x, primaryTarget.y - monster.y);
          Skills.applyDamage(monster, primaryTarget, dmg, monster.weaponType || 'crush', false, this.registry);
          monster.cooldowns[0] = 1.6; // Monster swing timer
        }
      }
    }
  }

  /**
   * Automatic weapon auto-attacks for Player and Companions
   */
  private updatePartyAutoAttacks(dt: number, player: EntityState): void {
    const monsters = this.registry.getMonsters();
    if (monsters.length === 0) return;

    const party = this.registry.getParty();
    const combatTarget = FormationAI.getPartyCombatTarget(player, monsters);

    for (const member of party) {
      if (member.isDead || member.cast || !CrowdControl.canAct(member)) continue;

      // Determine attack target: Player uses targetId, Companions use combatTarget or player's target
      let target: EntityState | null = null;
      if (member.category === 'player') {
        if (player.targetId) {
          const t = this.registry.get(player.targetId);
          if (t && t.category === 'monster' && !t.isDead) target = t;
        }
      } else {
        target = combatTarget;
        if (!target && player.targetId) {
          const t = this.registry.get(player.targetId);
          if (t && t.category === 'monster' && !t.isDead) target = t;
        }
      }

      if (!target) continue;

      // Range check: Only Ranger (Bow) is ranged auto-attack; Wizard, Cleric, Paladin, Monk, Bard swing melee weapons (Staff/Mace/Sword/Rapier/Fist)!
      const isRangedAuto = member.role === 'ranged_dps';
      const maxRange = isRangedAuto ? 6.5 : 2.1;
      const dist = this.registry.getDistance(member, target);

      if (dist <= maxRange) {
        if (!member.cooldowns[0] || member.cooldowns[0] <= 0) {
          this.executeAutoAttack(member, target);
        }
      }
    }
  }

  /**
   * Execute auto-attack swing/shot with visual attack animation, QUI speed scaling, and weapon skill gain
   */
  private executeAutoAttack(attacker: EntityState, target: EntityState): void {
    attacker.attackAnimTimer = 0.28; // Trigger swing/shot visual motion
    attacker.dir = this.calcDirection(target.x - attacker.x, target.y - attacker.y);

    const metrics = getDerivedCombatMetrics(attacker);
    let swingTimer = 1.5;
    let baseDmg = 24;

    if (attacker.role === 'tank') {
      swingTimer = 1.5;
      baseDmg = 28;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'cleave_arc',
        gx: target.x,
        gy: target.y,
      });
    } else if (attacker.role === 'monk_hybrid') {
      swingTimer = 1.1;
      baseDmg = 22;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'kick_whirl',
        gx: target.x,
        gy: target.y,
      });
    } else if (attacker.role === 'ranged_dps') {
      swingTimer = 1.3;
      baseDmg = 26;
      EventBus.emit('PROJECTILE_SPAWNED', {
        type: 'arrow',
        fromX: attacker.x,
        fromY: attacker.y,
        toX: target.x,
        toY: target.y,
        speed: 14.0,
      });
    } else if (attacker.role === 'caster_dps') {
      // 마법사 기본평타: 지팡이 휘두르기 (크러시 타격!)
      swingTimer = 1.6;
      baseDmg = 20;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'shield_impact',
        gx: target.x,
        gy: target.y,
      });
    } else if (attacker.role === 'buffer') {
      swingTimer = 1.4;
      baseDmg = 22;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'cleave_arc',
        gx: target.x,
        gy: target.y,
      });
    } else if (attacker.role === 'healer') {
      swingTimer = 1.6;
      baseDmg = 22;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'shield_impact',
        gx: target.x,
        gy: target.y,
      });
    }

    // Apply Speed (QUI) stat & buff reduction to weapon swing timer!
    attacker.cooldowns[0] = Math.max(0.45, Number((swingTimer * metrics.swingTimeMult).toFixed(2)));
    const scaledDmg = Math.round(baseDmg * metrics.physDamageMult);

    // Subtle weapon skill gain on auto-attack
    const wSkill = attacker.weaponType === 'slash' ? 'slash' : attacker.weaponType === 'thrust' ? 'thrust' : 'crush';
    if (Math.random() < 0.25) {
      practiceSelfSkill(attacker, wSkill, 0.2);
    }

    Skills.applyDamage(attacker, target, scaledDmg, attacker.weaponType || 'slash', false, this.registry);
  }

  private calcDirection(dx: number, dy: number): Direction8 {
    const angle = Math.atan2(dy, dx);
    const deg = (angle * 180) / Math.PI;
    if (deg >= -22.5 && deg < 22.5) return 'E';
    if (deg >= 22.5 && deg < 67.5) return 'SE';
    if (deg >= 67.5 && deg < 112.5) return 'S';
    if (deg >= 112.5 && deg < 157.5) return 'SW';
    if (deg >= 157.5 || deg < -157.5) return 'W';
    if (deg >= -157.5 && deg < -112.5) return 'NW';
    if (deg >= -112.5 && deg < -67.5) return 'N';
    return 'NE';
  }

  /**
   * Quick player action trigger
   */
  public triggerPlayerSkill(slot: number): boolean {
    const player = this.registry.getPlayer();
    if (!player) return false;
    return Skills.useSkill(player, slot, this.registry);
  }

  /**
   * Cycle next enemy target for Tab key
   */
  public cycleTarget(forward = true): EntityState | null {
    const player = this.registry.getPlayer();
    if (!player) return null;

    const monsters = this.registry.getMonsters();
    if (monsters.length === 0) {
      player.targetId = null;
      return null;
    }

    // Sort by distance to player
    monsters.sort((a, b) => this.registry.getDistance(player, a) - this.registry.getDistance(player, b));

    const currentIndex = monsters.findIndex((m) => m.id === player.targetId);
    let nextIndex = 0;
    if (currentIndex >= 0) {
      nextIndex = (currentIndex + (forward ? 1 : -1) + monsters.length) % monsters.length;
    }

    // Cancel stick if cycling to another target
    if (this.isStickActive) {
      this.cancelStick(false);
    }

    player.targetId = monsters[nextIndex].id;
    EventBus.emit('TARGET_CHANGED', { targetId: player.targetId });
    return monsters[nextIndex];
  }

  /**
   * Toggle corridor single-file formation mode
   */
  public toggleCorridorFormation(): boolean {
    const isCorridor = FormationAI.toggleCorridorMode();
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: isCorridor
        ? '🚶 [파티 대형] 좁은 통로 모드 활성화: 일렬 종대(Single File)로 전환합니다.'
        : '🎳 [파티 대형] 볼링핀 1-2-3 정삼각형 대형으로 복귀합니다.',
      category: 'system',
    });
    return isCorridor;
  }

  public isCorridorFormation(): boolean {
    return FormationAI.isCorridorMode();
  }

  /**
   * Set target directly (e.g. clicking ally in party frame or enemy)
   */
  public selectTarget(targetId: string | null): void {
    const player = this.registry.getPlayer();
    if (!player) return;
    if (this.isStickActive && player.targetId !== targetId) {
      this.cancelStick(false);
    }
    player.targetId = targetId;
    EventBus.emit('TARGET_CHANGED', { targetId });
  }

  /**
   * Check if target auto-follow (Stick) is currently active
   */
  public isStick(): boolean {
    return this.isStickActive;
  }

  /**
   * Toggle auto-follow (Stick) on current target [F Key]
   */
  public toggleStick(): boolean {
    const player = this.registry.getPlayer();
    if (!player) return false;

    if (!player.targetId) {
      this.isStickActive = false;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: '⚠️ 따라갈 대상(타겟)이 없습니다. Tab 키나 마우스로 먼저 대상을 지정하세요.',
        category: 'system',
      });
      return false;
    }

    const target = this.registry.get(player.targetId);
    if (!target || target.isDead) {
      this.isStickActive = false;
      player.targetId = null;
      EventBus.emit('TARGET_CHANGED', { targetId: null });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: '⚠️ 대상이 유효하지 않거나 이미 사망했습니다.',
        category: 'system',
      });
      return false;
    }

    this.isStickActive = !this.isStickActive;

    EventBus.emit('STICK_STATUS_CHANGED', {
      active: this.isStickActive,
      targetId: this.isStickActive ? target.id : null,
      targetName: target.name,
    });

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: this.isStickActive
        ? `🏃 [${target.name}] 자동 따라가기(Stick) 시작! [F 키로 해제]`
        : `🛑 [${target.name}] 자동 따라가기(Stick) 해제`,
      category: 'system',
    });

    return this.isStickActive;
  }

  /**
   * Cancel auto-follow (Stick). Optionally clear target focus if target died or was lost.
   */
  public cancelStick(clearTarget = false, reason?: string): void {
    const wasActive = this.isStickActive;
    this.isStickActive = false;

    const player = this.registry.getPlayer();
    if (clearTarget && player && player.targetId) {
      player.targetId = null;
      EventBus.emit('TARGET_CHANGED', { targetId: null });
    }

    if (wasActive) {
      EventBus.emit('STICK_STATUS_CHANGED', { active: false, targetId: null });
      if (reason === 'target_dead_or_lost') {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: '💨 대상이 사망하거나 사라져 자동 따라가기 및 포커스가 종료되었습니다.',
          category: 'system',
        });
      }
    }
  }
}
